import { useContext } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { AppContext } from "context/appContext";
import { Chip, SxProps, useTheme } from "@mui/material";
import { Label } from "@mui/icons-material";
import { formatLabel } from "utils";
import { useTranslation } from "react-i18next";

interface FilterLabelAutocompleteProps {
  filterLabels: string[];
  setFilterLabels: (labels: string[]) => void;
  disabled?: boolean;
  readonly?: boolean;
  margin?: "dense" | "normal" | "none";
  sx?: SxProps;
  canCreate?: boolean;
}

export const DEFAULT_PROPERTY_LABELS: string[] = [
  "patrol",
  "request only",
  "permit",
];

export default function FilterLabelAutocomplete({
  filterLabels,
  setFilterLabels,
  disabled = false,
  readonly = false,
  margin = "dense",
  sx,
  canCreate = false,
}: FilterLabelAutocompleteProps) {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const theme = useTheme();

  const options = state.tenant?.data?.filterLabels || DEFAULT_PROPERTY_LABELS;

  return (
    <Autocomplete
      disabled={disabled}
      value={filterLabels}
      sx={sx}
      onChange={(event, newValue) => {
        setFilterLabels(newValue);
      }}
      renderTags={(value: readonly string[], getTagProps) =>
        value.map((option: string, index: number) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              icon={
                <Label
                  fontSize="small"
                  htmlColor={theme.palette.primary.contrastText}
                />
              }
              variant="filled"
              color={"primary"}
              size={"medium"}
              sx={{ fontWeight: "bold" }}
              label={formatLabel(option)}
              key={key}
              {...tagProps}
            />
          );
        })
      }
      id="filter-label-autocomplete"
      multiple
      options={options}
      getOptionLabel={(option) => option}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      renderOption={(props, option) => {
        const { ...optionProps } = props;
        return (
          <li {...optionProps} key={option}>
            {formatLabel(option)}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          margin={margin}
          label={t("property.data.filterLabels.label_plural", {
            ns: "entities",
          })}
          InputProps={{ ...params.InputProps, readOnly: readonly }}
        />
      )}
    />
  );
}
